import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Edit,
  Download,
  Search,
  Eye,
  Plus,
  Building,
  User,
  CheckCircle,
  AlertCircle,
  X,
  Trash2,
  FileText,
  Check,
  XCircle,
  Clock,
  Send,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import quotationDashboardService from "../../../api/crm/quotation-dashboard/quotationDashboardService";
import quotationTrackingService from "../../../api/crm/quotations/quotationTrackingService";
import SendQuoteModal from "./components/SendQuoteModal";
import { useMutation } from "@tanstack/react-query";
import PermissionGate from "../../../components/common/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import opportunityService from "../../../api/crm/opportunities/opportunityService";
import quotationService from "../../../api/crm/quotations/quotationService";
import customerService from "../../../api/crm/customers/customerService";
import { formatDate } from "../../../utils/stringUtils";
import {
  getQuotationStatusClasses,
  formatQuotationStatus,
} from "../../../utils/quotationStatusUtils";
import {
  formatIndianAmount,
  formatIndianNumber,
} from "../../../utils/amountFormatter";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import { toast } from "react-hot-toast";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import teamManagementService from "../../../api/crm/team-management/teamManagementService";

const CreateQuotesList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial filter values from URL (for persistence across refresh)
  const initialSearch = searchParams.get("search") || "";
  const initialStatus = searchParams.get("status") || "all";
  const initialStartDate = searchParams.get("startDate") || "";
  const initialEndDate = searchParams.get("endDate") || "";
  const initialPage = Number(searchParams.get("page")) || 1;
  const initialLimit = Number(searchParams.get("limit")) || 10;
  const initialSortBy = searchParams.get("sortBy") || "created";
  const initialSortDir = searchParams.get("sortOrder") || "desc";
  const isInitialSearchResetMount = useRef(true);

  // Quote list pagination state
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(initialSortBy);
  const [sortDirection, setSortDirection] = useState(initialSortDir);

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);

  // Bulk delete state
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Send Modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendQuote, setSendQuote] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);

  // Selection & Export State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Status filter state
  const [filterStatus, setFilterStatus] = useState(initialStatus);
  const [dateRange, setDateRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });

  // Assignee filter state
  const isSalesExecutive = currentUser?.role === 'sales_executive' || currentUser?.role === 'export_executive' || currentUser?.role === 'business_relationship_manager' || currentUser?.role === 'business_development_executive';
  const isManager = currentUser?.role === 'sales_manager' || currentUser?.role === 'export_manager';

  const currentUserId =
    currentUser?.id ||
    currentUser?.userId ||
    (() => {
      try {
        const persisted = JSON.parse(localStorage.getItem('user_data') || '{}');
        return persisted?.id || persisted?.userId || persisted?._id;
      } catch (e) {
        return currentUser?._id;
      }
    })();

  const defaultAssignee = isManager && currentUserId ? currentUserId : 'all';
  const initialAssignee = searchParams.get("owner") || defaultAssignee;
  const [filterAssignee, setFilterAssignee] = useState(initialAssignee);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeFilterSearchTerm, setAssigneeFilterSearchTerm] = useState('');
  const [debouncedAssigneeSearchTerm, setDebouncedAssigneeSearchTerm] = useState('');
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeDropdownRef = useRef(null);

  // Debounce assignee search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAssigneeSearchTerm(assigneeFilterSearchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeFilterSearchTerm]);

  // KPI state
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpis, setKpis] = useState({
    totalQuotations: 0,
    acceptedCount: 0,
    rejectedCount: 0,
    expiredCount: 0,
    sentCount: 0,
    seenCount: 0,
    draftCount: 0,
    expiringSoon: 0,
  });

  // Opportunity Selection Modal states
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const searchInputRef = useRef(null); // Ref for search input focus
  const [modalActiveTab, setModalActiveTab] = useState("lead"); // 'lead' or 'customers'
  const [opportunitySearchTerm, setOpportunitySearchTerm] = useState("");
  const [debouncedOpportunitySearchTerm, setDebouncedOpportunitySearchTerm] =
    useState("");
  const [opportunityPagination, setOpportunityPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalOpportunities: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [opportunityItemsPerPage, setOpportunityItemsPerPage] = useState(10);

  // Customer Selection Modal states
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [debouncedCustomerSearchTerm, setDebouncedCustomerSearchTerm] =
    useState("");
  const [customerPagination, setCustomerPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalCustomers: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [customerItemsPerPage, setCustomerItemsPerPage] = useState(10);

  // Fetch KPIs
  useEffect(() => {
    const fetchKPIs = async () => {
      setKpisLoading(true);
      try {
        const result = await quotationDashboardService.getQuotationKPIs({
          startDate: dateRange.startDate || undefined,
          endDate: dateRange.endDate || undefined,
          search: debouncedSearchTerm || undefined,
          owner: filterAssignee !== "all" ? filterAssignee : undefined,
        });
        if (result.success) {
          setKpis(result.kpis);
        }
      } catch (error) {
      } finally {
        setKpisLoading(false);
      }
    };

    fetchKPIs();
  }, [refreshKey, dateRange, debouncedSearchTerm, filterAssignee]);

  // React Query: Fetch CRM users for assignee filter with backend search
  const {
    data: assigneeUsersData,
    isLoading: loadingAssigneeUsers,
    isFetching: isFetchingAssigneeUsers,
  } = useQuery({
    queryKey: [
      'crmAssigneeUsers',
      isManager ? (currentUserId || 'manager') : 'all',
      debouncedAssigneeSearchTerm,
    ],
    queryFn: async () => {
      const params = {
        roleFilter: 'sales',
        limit: 100,
        ...(debouncedAssigneeSearchTerm ? { search: debouncedAssigneeSearchTerm } : {}),
      };

      if (isManager) {
        params.managerId = currentUserId;
      }

      const result = await teamManagementService.getCRMUsers(params);
      return result?.success && result.users ? result.users : [];
    },
    enabled: !isSalesExecutive && (isManager ? !!currentUserId : true),
    staleTime: 0,
  });

  const loadingAssigneeFilter = loadingAssigneeUsers || isFetchingAssigneeUsers;

  // Derive filtered assignee options based on fetched data and search term
  const filteredAssigneeOptions = useMemo(() => {
    if (!assigneeUsersData) return [];

    const lowerTerm = debouncedAssigneeSearchTerm.toLowerCase();

    if (isManager) {
      const executives = assigneeUsersData
        .filter(u => ['sales_executive', 'export_executive', 'business_relationship_manager', 'business_development_executive'].includes(u.role))
        .map(u => ({
          id: u.user || u._id || u.id,
          name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown',
          email: u.email || '',
          role: u.role || ''
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const selfOption = {
        id: currentUserId,
        name: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.name || 'Me',
        email: currentUser?.email || '',
        role: currentUser?.role || ''
      };

      const myTeamOption = {
        id: 'my_team',
        name: 'My Team',
        email: '',
        role: 'system'
      };

      const prefixOptions = [];
      if (!debouncedAssigneeSearchTerm || 'my team'.includes(lowerTerm)) {
        prefixOptions.push(myTeamOption);
      }
      if (!debouncedAssigneeSearchTerm || 'unassigned'.includes(lowerTerm)) {
        prefixOptions.push({ id: 'unassigned', name: 'Unassigned', role: 'system' });
      }
      if (!debouncedAssigneeSearchTerm || selfOption.name.toLowerCase().includes(lowerTerm) || (selfOption.email && selfOption.email.toLowerCase().includes(lowerTerm))) {
        prefixOptions.push(selfOption);
      }

      return [...prefixOptions, ...executives];
    } else {
      const mappedUsers = assigneeUsersData.map(u => ({
        id: u.user || u._id || u.id,
        name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown',
        email: u.email || '',
        role: u.role || ''
      })).sort((a, b) => a.name.localeCompare(b.name));

      const prefixOptions = [];
      if (!debouncedAssigneeSearchTerm || 'unassigned'.includes(lowerTerm)) {
        prefixOptions.push({ id: 'unassigned', name: 'Unassigned', role: 'system' });
      }

      return [...prefixOptions, ...mappedUsers];
    }
  }, [assigneeUsersData, debouncedAssigneeSearchTerm, isManager, currentUserId, currentUser]);

  // Maintain assigneeOptions cache for button display label
  useEffect(() => {
    if (filteredAssigneeOptions.length > 0) {
      setAssigneeOptions(prev => {
        const map = new Map();
        prev.forEach(item => { if (item && item.id) map.set(item.id, item); });
        filteredAssigneeOptions.forEach(item => { if (item && item.id) map.set(item.id, item); });
        if (map.size === prev.length) return prev;
        return Array.from(map.values());
      });
    }
  }, [filteredAssigneeOptions]);

  // Calculate assignee dropdown position
  useEffect(() => {
    if (openAssigneeDropdown && assigneeDropdownRef.current) {
      const rect = assigneeDropdownRef.current.getBoundingClientRect();
      setAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openAssigneeDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-assignee-filter]');
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setOpenAssigneeDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounce search term for quotes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
      // Skip the page reset on the initial mount so the URL-restored page is kept
      if (isInitialSearchResetMount.current) {
        isInitialSearchResetMount.current = false;
        return;
      }
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Sync filter/pagination/sort state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'status', 'owner', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterAssignee && filterAssignee !== 'all') params.set('owner', filterAssignee);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortColumn && sortColumn !== 'created') params.set('sortBy', sortColumn);
      if (sortDirection && sortDirection !== 'desc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    filterStatus,
    filterAssignee,
    dateRange,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortDirection,
    setSearchParams,
  ]);

  // Debounce search term for opportunities
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOpportunitySearchTerm(opportunitySearchTerm);
      setOpportunityPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [opportunitySearchTerm]);

  // Debounce search term for customers
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerSearchTerm(customerSearchTerm);
      setCustomerPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearchTerm]);

  // React Query: Fetch all created quotes
  const {
    data: quotesData,
    isLoading: loadingQuotes,
    isError: quotesError,
    error: quotesErrorData,
  } = useQuery({
    queryKey: [
      "quotationList",
      "all",
      debouncedSearchTerm,
      filterStatus,
      currentPage,
      itemsPerPage,
      dateRange,
      filterAssignee,
      refreshKey,
    ],
    queryFn: async () => {
      const filters = {
        search: debouncedSearchTerm.trim() || undefined, // Send search to backend
        status:
          filterStatus !== "all"
            ? filterStatus
            : "draft,finalized,sent,seen,accepted,rejected,expired,revoked,order,ordered,converted", // Include all statuses including order and ordered status
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        owner: filterAssignee !== "all" ? filterAssignee : undefined,
      };

      const result = await quotationDashboardService.getQuotationList(filters, {
        tab: "all", // Get all quotes, not just my-quotes
        page: currentPage,
        limit: itemsPerPage,
      });

      if (result.success) {
        return {
          quotations: result.quotations || [],
          pagination: result.pagination || null,
        };
      } else {
        throw new Error(result.error || "Failed to fetch quotes");
      }
    },
    staleTime: 0,
    retry: 2,
  });

  const quotes = quotesData?.quotations || [];
  const pagination = quotesData?.pagination || null;

  const ALL_QUOTE_STATUSES_EXPORT =
    "draft,finalized,sent,seen,accepted,rejected,expired,revoked,order,ordered,converted";

  const baseExportFilters = useMemo(
    () => ({
      search: debouncedSearchTerm.trim() || undefined,
      status:
        filterStatus !== "all" ? filterStatus : ALL_QUOTE_STATUSES_EXPORT,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
      owner: filterAssignee !== "all" ? filterAssignee : undefined,
    }),
    [
      debouncedSearchTerm,
      filterStatus,
      dateRange.startDate,
      dateRange.endDate,
      filterAssignee,
    ],
  );

  const sortedQuotes = useMemo(() => {
    if (!sortColumn || !quotes || quotes.length === 0) return quotes;

    return [...quotes].sort((a, b) => {
      let aValue;
      let bValue;

      if (sortColumn === "quoteId") {
        aValue = a.quoteId || a.quoteNumber || "";
        bValue = b.quoteId || b.quoteNumber || "";
      } else if (sortColumn === "companyName") {
        aValue = a.companyName || "N/A";
        bValue = b.companyName || "N/A";
      } else if (sortColumn === "value") {
        aValue = Number(a.value) || 0;
        bValue = Number(b.value) || 0;
      } else if (sortColumn === "status") {
        aValue = a.status || "";
        bValue = b.status || "";
      } else if (sortColumn === "createdBy") {
        aValue = (typeof a.createdBy === 'object' && a.createdBy ? (a.createdBy.name || `${a.createdBy.firstName || ''} ${a.createdBy.lastName || ''}`.trim()) : a.createdByName) || (typeof a.owner === 'string' && a.owner !== 'N/A' ? a.owner : '');
        bValue = (typeof b.createdBy === 'object' && b.createdBy ? (b.createdBy.name || `${b.createdBy.firstName || ''} ${b.createdBy.lastName || ''}`.trim()) : b.createdByName) || (typeof b.owner === 'string' && b.owner !== 'N/A' ? b.owner : '');
      } else if (sortColumn === "created") {
        aValue = new Date(a.createdAt || a.created_at || 0).getTime();
        bValue = new Date(b.createdAt || b.created_at || 0).getTime();
      } else if (sortColumn === "expiredAt") {
        aValue = new Date(a.expiresOn || 0).getTime();
        bValue = new Date(b.expiresOn || 0).getTime();
      } else {
        aValue = a[sortColumn] || "";
        bValue = b[sortColumn] || "";
      }

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [quotes, sortColumn, sortDirection]);

  // Helper function to check if a quote is expiring soon (within 7 days)
  // Exclude: expired, accepted, order, ordered
  const isExpiringSoon = (quote) => {
    if (!quote.expiresOn) {
      return false;
    }

    // If already expired, it's not "expiring soon"
    if (quote.isExpired) {
      return false;
    }

    // Exclude these statuses: expired, accepted, order, ordered
    const excludedStatuses = [
      "expired",
      "accepted",
      "order",
      "ordered",
      "converted",
    ];
    if (excludedStatuses.includes(quote.status?.toLowerCase())) {
      return false;
    }

    const expireDate = new Date(quote.expiresOn);
    const today = new Date();
    const diffTime = expireDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Return true if expiring within 0-7 days
    return diffDays <= 7 && diffDays >= 0;
  };
  // React Query: Fetch opportunities without quotations for modal
  const {
    data: opportunitiesData,
    isLoading: loadingOpportunities,
    isError: opportunitiesError,
    error: opportunitiesErrorData,
    refetch: refetchOpportunities,
  } = useQuery({
    queryKey: [
      "opportunities",
      "list",
      "withoutQuotations",
      debouncedOpportunitySearchTerm,
      opportunityPagination.page,
      opportunityItemsPerPage,
      currentUser?.id || currentUser?._id,
    ],
    queryFn: async () => {
      const result =
        await opportunityService.getConvertedOpportunitiesWithoutQuotations({
          search: debouncedOpportunitySearchTerm.trim() || undefined,
          page: opportunityPagination.page,
          limit: opportunityItemsPerPage,
          userId: currentUser?.id || currentUser?._id,
        });

      if (result.success) {
        return {
          opportunities: result.opportunities || [],
          pagination: result.pagination || {
            page: opportunityPagination.page,
            limit: opportunityItemsPerPage,
            totalPages: 1,
            totalOpportunities: 0,
            hasNextPage: false,
            hasPrevPage: false,
          },
        };
      } else {
        throw new Error(result.error || "Failed to load opportunities");
      }
    },
    enabled: showOpportunityModal && modalActiveTab === "lead", // Only fetch when modal is open and Lead tab is active
    staleTime: 0,
    retry: 2,
  });

  // React Query: Fetch customers for modal
  const {
    data: customersData,
    isLoading: loadingCustomers,
    isError: customersError,
    error: customersErrorData,
    refetch: refetchCustomers,
  } = useQuery({
    queryKey: [
      "customers",
      "list",
      debouncedCustomerSearchTerm,
      customerPagination.page,
      customerItemsPerPage,
      currentUser?.id || currentUser?._id,
    ],
    queryFn: async () => {
      const result = await customerService.getCustomers({
        search: debouncedCustomerSearchTerm.trim() || undefined,
        status: "customer", // Only fetch active customers
        page: customerPagination.page,
        limit: customerItemsPerPage,
        userId: currentUser?.id || currentUser?._id,
      });

      if (result.success) {
        return {
          customers: result.customers || [],
          pagination: result.pagination || {
            page: customerPagination.page,
            limit: customerItemsPerPage,
            totalPages: 1,
            total: 0,
          },
        };
      } else {
        throw new Error(result.error || "Failed to load customers");
      }
    },
    enabled: showOpportunityModal && modalActiveTab === "customers", // Only fetch when modal is open and Customers tab is active
    staleTime: 0,
    retry: 2,
  });

  const opportunities = opportunitiesData?.opportunities || [];
  const opportunityPaginationData =
    opportunitiesData?.pagination || opportunityPagination;
  const opportunityError = opportunitiesError
    ? opportunitiesErrorData?.message ||
    "Failed to load opportunities. Please try again."
    : null;

  const customers = customersData?.customers || [];
  const customerPaginationData =
    customersData?.pagination || customerPagination;
  const customerError = customersError
    ? customersErrorData?.message ||
    "Failed to load customers. Please try again."
    : null;

  // Update opportunity pagination state when data changes
  useEffect(() => {
    if (opportunitiesData?.pagination) {
      setOpportunityPagination((prev) => ({
        page: opportunitiesData.pagination.currentPage || prev.page,
        limit: opportunitiesData.pagination.limit || prev.limit,
        totalPages: opportunitiesData.pagination.totalPages || 1,
        totalOpportunities:
          opportunitiesData.pagination.totalOpportunities || 0,
        hasNextPage: opportunitiesData.pagination.hasNextPage || false,
        hasPrevPage: opportunitiesData.pagination.hasPrevPage || false,
      }));
    }
  }, [opportunitiesData]);

  // Update customer pagination state when data changes
  useEffect(() => {
    if (customersData?.pagination) {
      const total = customersData.pagination.total || 0;
      const totalPages = customersData.pagination.totalPages || 1;
      const currentPage =
        customersData.pagination.page || customerPagination.page;

      setCustomerPagination((prev) => ({
        page: currentPage,
        limit: customersData.pagination.limit || prev.limit,
        totalPages: totalPages,
        totalCustomers: total,
        hasNextPage: currentPage < totalPages,
        hasPrevPage: currentPage > 1,
      }));
    }
  }, [customersData]);

  // Status colors and labels are centralized in quotationStatusUtils

  // Skeleton loading components
  const TableRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-4"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-32"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-20"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-6 bg-gray-200 rounded-full w-20"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-6 bg-gray-200 rounded-full w-20"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-28"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-28"></div>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <div className="h-8 bg-gray-200 rounded w-20"></div>
          <div className="h-8 bg-gray-200 rounded w-24"></div>
        </div>
      </td>
    </tr>
  );

  const OpportunityRowSkeleton = () => (
    <div className="p-4 border-b border-gray-200 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-32"></div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-24"></div>
      </div>
    </div>
  );

  const handleUpdateQuote = (quote) => {
    const idToUse = quote._id || quote.quoteId || quote.id;
    // Navigate to Quote Builder with quotationId for editing
    navigate(`/crm/quotation-proposal/quote-builder?quotationId=${idToUse}`);
  };

  const handleSendQuote = (quote) => {
    const idToUse = quote._id || quote.quoteId || quote.id;
    navigate(`/crm/quotation-proposal/send-track/${idToUse}?page=quote`);
  };

  const handleOpenSendModal = (quote) => {
    setSendQuote(quote);
    setShowSendModal(true);
  };

  const sendQuotationMutation = useMutation({
    mutationFn: ({ isResend, quoteId, email, message }) => {
      if (isResend) {
        return quotationTrackingService.resendQuote(quoteId, email, message);
      } else {
        return quotationService.sendQuotation(quoteId, {
          to: email,
          message: message || "",
        });
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        // Invalidate list query
        queryClient.invalidateQueries(["quotationList"]);
        setShowSendModal(false);
        setSendQuote(null);
        alert("Quotation sent successfully!");
      } else {
        const errorMsg = data.error || "Failed to send email";
        alert(`Email sending failed: ${errorMsg}`);
      }
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.responseMessage ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to send email";
      alert(`Email sending failed: ${errorMessage}`);
    },
  });

  const handleExecuteSend = ({ email, message, isResend }) => {
    if (!sendQuote) return;
    const quoteId = sendQuote._id || sendQuote.quoteId || sendQuote.id;

    sendQuotationMutation.mutate({
      isResend,
      quoteId,
      email,
      message,
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterChange = (newFilterStatus) => {
    setFilterStatus(newFilterStatus);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to page 1 when changing items per page
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    // items is the current page data
    const allSelected = items.every((item) =>
      selectedRows.has(item._id || item.quoteId),
    );

    setSelectedRows((prev) => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach((item) => newSelection.delete(item._id || item.quoteId));
      } else {
        items.forEach((item) => newSelection.add(item._id || item.quoteId));
      }
      return newSelection;
    });
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    toast.success("List refreshed");
  };

  const currentPageIdsForExport = useMemo(
    () =>
      sortedQuotes
        .map((q) => (q._id ? String(q._id) : ""))
        .filter(Boolean),
    [sortedQuotes],
  );

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === "currentPage"
          ? overrideCurrentPageIds ?? currentPageIdsForExport
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          module: "crm-create-quotes-list",
          format,
          scope,
          filters: {
            ...baseExportFilters,
            ...(overrideFilters != null ? overrideFilters : {}),
          },
          page: overridePage ?? currentPage,
          limit: overrideLimit ?? itemsPerPage,
          sortBy: "createdAt",
          sortOrder: "desc",
          listSortColumn: sortColumn || null,
          listSortDirection: sortDirection,
          selectedIds,
          currentPageIds: Array.isArray(pageIds) ? pageIds : [],
        }),
      },
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || "Export request failed";
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `quotations-export.${format === "xlsx" ? "xlsx" : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      currentPageIdsForExport,
      sortColumn,
      sortDirection,
    ],
  );

  const handleCreateQuotesSelectedFormatExport = useCallback(
    async (format) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) return;
      const ids = sortedQuotes
        .filter((q) => selectedRows.has(q._id || q.quoteId))
        .map((q) => (q._id ? String(q._id) : ""))
        .filter(Boolean);
      if (!ids.length) {
        toast.error("No selected rows to export.");
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: "selected",
          format,
          selectedIds: ids,
        });
        toast.success(
          `${ids.length} quotation${ids.length === 1 ? "" : "s"} exported.`,
        );
        setShowInlineSelectedFormats(false);
        setSelectedRows(new Set());
      } catch (error) {
        toast.error(error?.message || "Export failed.");
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport, sortedQuotes],
  );

  const handleCreateQuotesScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) return;

      const pageItems = sortedQuotes;
      const totalFiltered = Number(pagination?.totalRecords ?? 0);

      setExportLoading(true);
      try {
        if (scope === "currentPage" && !pageItems.length) {
          toast.error("No data on current page to export.");
          return;
        }

        if (scope === "filtered" && totalFiltered === 0) {
          toast.error("No quotations match the current filters.");
          return;
        }

        if (scope === "dateRange") {
          const rangeStart = meta?.startDate;
          const rangeEnd = meta?.endDate;
          if (!rangeStart || !rangeEnd) {
            toast.error("Please select start and end dates.");
            return;
          }
          await requestBackendExport({
            scope: "dateRange",
            format,
            overrideFilters: {
              search: baseExportFilters.search,
              status: baseExportFilters.status,
              startDate: rangeStart,
              endDate: rangeEnd,
            },
          });
        } else if (scope === "all") {
          await requestBackendExport({
            scope: "all",
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === "filtered") {
          await requestBackendExport({
            scope: "filtered",
            format,
          });
        } else if (scope === "currentPage") {
          await requestBackendExport({
            scope: "currentPage",
            format,
          });
        } else {
          return;
        }

        toast.success("Export downloaded.");
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || "Export failed. Please try again.");
      } finally {
        setExportLoading(false);
      }
    },
    [
      baseExportFilters,
      pagination?.totalRecords,
      sortedQuotes,
      requestBackendExport,
      sortColumn,
      sortDirection,
    ],
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true)),
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showInlineSelectedFormats]);

  // Handle items per page change for opportunities
  const handleOpportunityItemsPerPageChange = (limit) => {
    setOpportunityItemsPerPage(limit);
    setOpportunityPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleOpenOpportunityModal = () => {
    // Reset all states first
    setModalActiveTab("lead");
    setOpportunitySearchTerm("");
    setDebouncedOpportunitySearchTerm("");
    setCustomerSearchTerm("");
    setDebouncedCustomerSearchTerm("");
    setOpportunityPagination({
      page: 1,
      limit: 10,
      totalPages: 1,
      totalOpportunities: 0,
      hasNextPage: false,
      hasPrevPage: false,
    });
    setCustomerPagination({
      page: 1,
      limit: 10,
      totalPages: 1,
      totalCustomers: 0,
      hasNextPage: false,
      hasPrevPage: false,
    });
    setOpportunityItemsPerPage(10);
    setCustomerItemsPerPage(10);
    // Open modal - React Query will handle the API call
    setShowOpportunityModal(true);
  };

  const handleCloseOpportunityModal = () => {
    setShowOpportunityModal(false);
    setModalActiveTab("lead");
    setOpportunitySearchTerm("");
    setDebouncedOpportunitySearchTerm("");
    setCustomerSearchTerm("");
    setDebouncedCustomerSearchTerm("");
    setOpportunityPagination({
      page: 1,
      limit: 10,
      totalPages: 1,
      totalOpportunities: 0,
      hasNextPage: false,
      hasPrevPage: false,
    });
    setCustomerPagination({
      page: 1,
      limit: 10,
      totalPages: 1,
      totalCustomers: 0,
      hasNextPage: false,
      hasPrevPage: false,
    });
    setOpportunityItemsPerPage(10);
    setCustomerItemsPerPage(10);
  };

  // Handle items per page change for customers
  const handleCustomerItemsPerPageChange = (limit) => {
    setCustomerItemsPerPage(limit);
    setCustomerPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSelectOpportunity = (opportunityId) => {
    handleCloseOpportunityModal();
    navigate(
      `/crm/quotation-proposal/quote-builder?opportunityId=${opportunityId}`,
    );
  };

  const handleSelectCustomer = (customerId) => {
    handleCloseOpportunityModal();
    navigate(`/crm/quotation-proposal/quote-builder?customerId=${customerId}`);
  };

  const handleDeleteClick = (quote) => {
    setQuoteToDelete(quote);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!quoteToDelete) return;

    try {
      const result = await quotationService.deleteQuotation(
        quoteToDelete._id || quoteToDelete.quoteId || quoteToDelete.id,
      );

      if (result.success) {
        queryClient.invalidateQueries(["quotationList"]);
        setShowDeleteModal(false);
        setQuoteToDelete(null);
        toast.success("Quotation deleted successfully");
      } else {
        toast.error(result.error || "Failed to delete quotation");
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete quotation");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setQuoteToDelete(null);
  };

  // Bulk delete handlers
  const handleBulkDeleteClick = () => {
    if (selectedRows.size > 0) {
      setShowBulkDeleteModal(true);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setIsBulkDeleting(true);
    const ids = Array.from(selectedRows);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => quotationService.deleteQuotation(id)),
      );
      const succeeded = results.filter(
        (r) => r.status === "fulfilled" && r.value?.success,
      ).length;
      const failed = ids.length - succeeded;

      queryClient.invalidateQueries(["quotationList"]);
      setSelectedRows(new Set());
      setShowBulkDeleteModal(false);

      if (failed === 0) {
        toast.success(
          `${succeeded} quotation${succeeded > 1 ? "s" : ""} deleted successfully!`,
        );
      } else {
        toast.error(
          `${succeeded} deleted, ${failed} failed. Please try again.`,
        );
      }
    } catch (error) {
      toast.error("Failed to delete quotations");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleConvertToOrder = (quote) => {
    const idToUse = quote._id || quote.quoteId || quote.id;
    if (!idToUse) {
      return;
    }
    // Navigate to convert order page
    navigate(`/crm/quotation-proposal/convert-order/${idToUse}`);
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Quotes</h1>
          <p className="text-gray-600">
            View and manage all created quotations
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedRows.size > 0 && (
            <PermissionGate permission="quotation_management" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDeleteClick}
                loading={isBulkDeleting}
                className="h-10"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="quotation_management" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading || loadingQuotes}
                className={`w-full sm:w-auto h-10${selectedRows.size > 0
                    ? " bg-blue-50 border-blue-200 text-blue-600"
                    : ""
                  }`}
              >
                {selectedRows.size > 0
                  ? `Export Selected (${selectedRows.size})`
                  : "Export"}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleCreateQuotesSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="quotation_management" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleOpenOpportunityModal}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Create Quote
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
        {kpisLoading ? (
          Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse flex-1 min-w-0"
            >
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">
                    Quotations
                  </p>
                  <p className="text-xl font-bold text-gray-900 truncate">
                    {formatIndianNumber(kpis.totalQuotations)}
                  </p>
                </div>
                <div className="p-2 bg-blue-100 rounded-full flex-shrink-0 ml-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">
                    Accepted
                  </p>
                  <p className="text-xl font-bold text-green-600 truncate">
                    {formatIndianNumber(kpis.acceptedCount)}
                  </p>
                </div>
                <div className="p-2 bg-green-100 rounded-full flex-shrink-0 ml-2">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">
                    Rejected
                  </p>
                  <p className="text-xl font-bold text-red-600 truncate">
                    {formatIndianNumber(kpis.rejectedCount)}
                  </p>
                </div>
                <div className="p-2 bg-red-100 rounded-full flex-shrink-0 ml-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">
                    Expired
                  </p>
                  <p className="text-xl font-bold text-orange-600 truncate">
                    {formatIndianNumber(kpis.expiredCount)}
                  </p>
                </div>
                <div className="p-2 bg-orange-100 rounded-full flex-shrink-0 ml-2">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">
                    Sent
                  </p>
                  <p className="text-xl font-bold text-yellow-600 truncate">
                    {formatIndianNumber(kpis.sentCount)}
                  </p>
                </div>
                <div className="p-2 bg-yellow-100 rounded-full flex-shrink-0 ml-2">
                  <Send className="h-5 w-5 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">
                    Seen
                  </p>
                  <p className="text-xl font-bold text-purple-600 truncate">
                    {formatIndianNumber(kpis.seenCount)}
                  </p>
                </div>
                <div className="p-2 bg-purple-100 rounded-full flex-shrink-0 ml-2">
                  <Eye className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">
                    Draft
                  </p>
                  <p className="text-xl font-bold text-gray-600 truncate">
                    {formatIndianNumber(kpis.draftCount)}
                  </p>
                </div>
                <div className="p-2 bg-gray-100 rounded-full flex-shrink-0 ml-2">
                  <FileText className="h-5 w-5 text-gray-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">
                    Expiring Soon
                  </p>
                  <p className="text-xl font-bold text-amber-600 truncate">
                    {formatIndianNumber(kpis.expiringSoon)}
                  </p>
                </div>
                <div className="p-2 bg-amber-100 rounded-full flex-shrink-0 ml-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-row flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by quote ID, company..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="flex-shrink-0 min-w-[150px] px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="seen">Seen</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="order">Ready to Order</option>
            <option value="ordered">Placed Order</option>
            <option value="expired">Expired</option>
          </select>

          {/* Assignee Filter Dropdown */}
          {!isSalesExecutive && (
            <div className="relative">
              <button
                ref={assigneeDropdownRef}
                onClick={() => {
                  const isOpening = !openAssigneeDropdown;
                  setOpenAssigneeDropdown(isOpening);
                  if (isOpening) {
                    setAssigneeFilterSearchTerm('');
                  }
                }}
                disabled={loadingAssigneeFilter}
                className="px-3 py-2.5 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm min-w-[160px]"
              >
                <span className="truncate whitespace-nowrap mr-2">
                  {filterAssignee === 'all'
                    ? 'All Assignees'
                    : filterAssignee === 'my_team'
                      ? 'My Team'
                      : assigneeOptions.find(opt => opt.id === filterAssignee)?.name || 'All Assignees'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
              </button>

              {openAssigneeDropdown && (
                <div
                  data-dropdown-assignee-filter
                  className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                  style={{
                    top: `${assigneeDropdownPosition.top}px`,
                    left: `${assigneeDropdownPosition.left}px`,
                    minWidth: '240px',
                    maxWidth: '350px',
                    width: `${Math.max(240, assigneeDropdownPosition.width)}px`
                  }}
                >
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={assigneeFilterSearchTerm}
                        onChange={(e) => setAssigneeFilterSearchTerm(e.target.value)}
                        placeholder="Search assignees..."
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                    {loadingAssigneeFilter && filteredAssigneeOptions.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500 text-center">Loading...</div>
                    ) : filteredAssigneeOptions.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500 text-center">No assignees found</div>
                    ) : (
                      <div className="flex flex-col">
                        {filteredAssigneeOptions.map(option => (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                              setAssigneeOptions(prev => prev.some(o => o.id === option.id) ? prev : [...prev, option]);
                              setFilterAssignee(option.id);
                              setOpenAssigneeDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${filterAssignee === option.id ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                          >
                            <div className="truncate font-medium">{option.name}</div>
                            {option.email && <div className="text-xs text-gray-500 truncate">{option.email}</div>}
                            {option.role && option.role !== 'system' && <div className="text-xs text-gray-500 truncate capitalize">{option.role.replace(/_/g, ' ')}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(newRange) => {
              setDateRange(newRange);
              setCurrentPage(1);
            }}
            allowFutureDates={true}
          />

          {(searchQuery ||
            filterStatus !== "all" ||
            dateRange.startDate ||
            dateRange.endDate ||
            filterAssignee !== defaultAssignee) && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchQuery("");
                  setFilterStatus("all");
                  setDateRange({ startDate: "", endDate: "" });
                  setFilterAssignee(defaultAssignee);
                  setAssigneeFilterSearchTerm("");
                  setOpenAssigneeDropdown(false);
                  setCurrentPage(1);
                }}
                className="flex-shrink-0 whitespace-nowrap"
              >
                Clear
              </Button>
            )}
        </div>
      </div>

      {/* Quotes Table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-purple-600 focus:ring-purple-600 border-gray-300 rounded cursor-pointer"
                      checked={
                        sortedQuotes.length > 0 &&
                        sortedQuotes.every((quote) =>
                          selectedRows.has(quote._id || quote.quoteId),
                        )
                      }
                      onChange={() => handleSelectAll(sortedQuotes)}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("quoteId")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Quote ID</span>
                      {sortColumn === "quoteId" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("companyName")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Company</span>
                      {sortColumn === "companyName" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("value")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Value</span>
                      {sortColumn === "value" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === "status" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("priority")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Priority</span>
                      {sortColumn === "priority" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("createdBy")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created By</span>
                      {sortColumn === "createdBy" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("created")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {sortColumn === "created" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("expiredAt")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Expired At</span>
                      {sortColumn === "expiredAt" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingQuotes ? (
                  <>
                    {[...Array(5)].map((_, index) => (
                      <TableRowSkeleton key={index} />
                    ))}
                  </>
                ) : quotes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-6 py-12 text-center text-sm text-gray-500"
                    >
                      No quotes found
                    </td>
                  </tr>
                ) : (
                  sortedQuotes.map((quote) => {
                    const createdDate = quote.createdAt || quote.created_at;
                    const formattedDate = createdDate
                      ? formatDate(createdDate, { includeTime: true })
                      : "N/A";

                    const createdByName =
                      (typeof quote.createdBy === 'object' && quote.createdBy
                        ? (quote.createdBy.name || `${quote.createdBy.firstName || ''} ${quote.createdBy.lastName || ''}`.trim())
                        : quote.createdByName) ||
                      (typeof quote.owner === 'string' && quote.owner !== 'N/A' ? quote.owner : 'N/A');

                    const createdByEmail =
                      (typeof quote.createdBy === 'object' && quote.createdBy
                        ? quote.createdBy.email
                        : quote.createdByEmail) || '';

                    return (
                      <tr
                        key={quote.quoteId || quote._id}
                        className={`hover:bg-gray-50 ${isExpiringSoon(quote) ? "bg-red-100 border-l-4 border-red-500" : ""}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-purple-600 focus:ring-purple-600 border-gray-300 rounded cursor-pointer"
                            checked={selectedRows.has(
                              quote._id || quote.quoteId,
                            )}
                            onChange={() =>
                              handleSelectRow(quote._id || quote.quoteId)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center">
                            {quote.quoteId || "N/A"}
                          </div>
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap truncate max-w-20 text-sm text-gray-900 capitalize"
                          title={quote.companyName || "N/A"}
                        >
                          {quote.companyName || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {/* ₹{quote.value?.toLocaleString('en-IN') || '0'} */}
                          ₹{formatIndianAmount(quote.value) || "0"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span
                              className={`inline-flex items-center mr-auto px-2.5 py-0.5 rounded-full text-xs font-medium ${getQuotationStatusClasses(
                                quote.status,
                              )}`}
                            >
                              {formatQuotationStatus(quote.status)}
                            </span>
                            {quote.isExpired && (
                              <span className="text-[12px] text-red-600 mt-1 ml-1 italic font-medium">
                                (Expired)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {quote.priority && (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${quote.priority === "high"
                                  ? "bg-red-100 text-red-800 border border-red-200"
                                  : quote.priority === "normal"
                                    ? "bg-blue-100 text-blue-800 border border-blue-200"
                                    : "bg-green-100 text-green-800 border border-green-200"
                                }`}
                            >
                              {quote.priority}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{createdByName}</span>
                            {createdByEmail && (
                              <span className="text-xs text-gray-500">{createdByEmail}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formattedDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {quote.expiresOn
                            ? formatDate(quote.expiresOn, { includeTime: true })
                            : "N/A"}
                          {isExpiringSoon(quote) && (
                            <div className="text-xs text-yellow-600 mt-1">
                              Expires in{" "}
                              {Math.ceil(
                                (new Date(quote.expiresOn) - new Date()) /
                                (1000 * 60 * 60 * 24),
                              )}{" "}
                              days
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            {/* View & Send button - navigates to Send & Track page (viewing track) */}
                            <PermissionGate
                              permission="quotation_management"
                              action="read"
                            >
                              <button
                                onClick={() => handleSendQuote(quote)}
                                className="text-gray-600 hover:text-gray-900"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>

                            {/* Send/Resend Button */}
                            {/* Show Send for Draft, Resend for Sent/Seen/Rejected */}
                            {["draft", "sent", "seen", "rejected"].includes(
                              quote.status?.toLowerCase(),
                            ) && (
                                <PermissionGate
                                  permission="quotation_management"
                                  action="update"
                                >
                                  <button
                                    onClick={() => handleOpenSendModal(quote)}
                                    className="text-blue-600 hover:text-blue-900"
                                    title={
                                      quote.status?.toLowerCase() === "draft"
                                        ? "Send Quote"
                                        : "Resend Quote"
                                    }
                                  >
                                    <Send className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              )}
                            {/* Convert to Order button - only shown when status is accepted */}
                            {quote.status?.toLowerCase() === "accepted" && (
                              <PermissionGate
                                permission="quotation_management"
                                action="create"
                              >
                                <button
                                  onClick={() => handleConvertToOrder(quote)}
                                  className="text-purple-600 hover:text-purple-900"
                                  title="Convert to Order"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            {/* Update button - hidden when accepted, converted, order, or ordered */}
                            {![
                              "accepted",
                              "converted",
                              "order",
                              "ordered",
                            ].includes(quote.status?.toLowerCase()) && (
                                <PermissionGate
                                  permission="quotation_management"
                                  action="update"
                                >
                                  <button
                                    onClick={() => handleUpdateQuote(quote)}
                                    className="text-green-600 hover:text-green-900"
                                    title="Update Quote"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              )}
                            {/* Delete button - hidden when ordered */}
                            {quote.status?.toLowerCase() !== "ordered" && (
                              <PermissionGate
                                permission="quotation_management"
                                action="delete"
                              >
                                <button
                                  onClick={() => handleDeleteClick(quote)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Quote"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {(() => {
            const totalItems = Number(
              pagination?.totalRecords || quotes.length || 0,
            );
            const totalPages = Number(pagination?.totalPages || 1);
            const effectivePaginationLimit =
              pagination?.limit || itemsPerPage || 10;
            const currentPageNum = pagination?.page || currentPage || 1;
            // Show pagination only if total items exceed the limit (meaning there are multiple pages)
            const shouldShowPagination =
              !loadingQuotes && totalItems > effectivePaginationLimit;

            return shouldShowPagination ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                  {/* Results Count and Limit Selector */}
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-700">
                      Showing{" "}
                      {formatIndianNumber(
                        (currentPageNum - 1) * effectivePaginationLimit + 1,
                      )}{" "}
                      to{" "}
                      {formatIndianNumber(
                        Math.min(
                          currentPageNum * effectivePaginationLimit,
                          totalItems,
                        ),
                      )}{" "}
                      of {formatIndianNumber(totalItems)} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">
                        Items per page:
                      </label>
                      <select
                        value={itemsPerPage}
                        onChange={(e) =>
                          handleItemsPerPageChange(Number(e.target.value))
                        }
                        className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPageNum - 1)}
                      disabled={currentPageNum <= 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const pages = [];

                        if (totalPages <= 10) {
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push(1);

                          if (currentPageNum <= 4) {
                            for (let i = 2; i <= 5; i++) {
                              pages.push(i);
                            }
                            pages.push("ellipsis");
                            pages.push(totalPages);
                          } else if (currentPageNum >= totalPages - 3) {
                            pages.push("ellipsis");
                            for (let i = totalPages - 4; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            pages.push("ellipsis");
                            for (
                              let i = currentPageNum - 1;
                              i <= currentPageNum + 1;
                              i++
                            ) {
                              pages.push(i);
                            }
                            pages.push("ellipsis");
                            pages.push(totalPages);
                          }
                        }

                        return pages.map((page, index) => {
                          if (page === "ellipsis") {
                            return (
                              <span
                                key={`ellipsis-${index}`}
                                className="px-2 text-gray-500"
                              >
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                  ? "bg-agrosaf-green text-white"
                                  : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPageNum + 1)}
                      disabled={currentPageNum >= totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
        </div>
      </div>

      {/* Opportunity Selection Modal */}
      {showOpportunityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-auto max-h-[90vh] overflow-hidden flex flex-col modal-body">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Select for Quote
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Choose a lead opportunity or customer to create a quotation
                </p>
              </div>
              <button
                onClick={handleCloseOpportunityModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 border-b border-gray-200">
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setModalActiveTab("lead")}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${modalActiveTab === "lead"
                      ? "border-agrosaf-green text-agrosaf-green"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Opportunities
                </button>
                <button
                  type="button"
                  onClick={() => setModalActiveTab("customers")}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${modalActiveTab === "customers"
                      ? "border-agrosaf-green text-agrosaf-green"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Customers
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 modal-body">
              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    autoFocus
                    placeholder={
                      modalActiveTab === "lead"
                        ? "Search by Company Name, Lead Name, Email, or Phone"
                        : "Search by Customer Name, Company Name, Email, Phone, or Customer ID"
                    }
                    value={
                      modalActiveTab === "lead"
                        ? opportunitySearchTerm
                        : customerSearchTerm
                    }
                    onChange={(e) => {
                      if (modalActiveTab === "lead") {
                        setOpportunitySearchTerm(e.target.value);
                      } else {
                        setCustomerSearchTerm(e.target.value);
                      }
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  />
                </div>
              </div>

              {/* Lead Tab Content */}
              {modalActiveTab === "lead" && (
                <>
                  {/* Loading State */}
                  {loadingOpportunities && (
                    <div className="divide-y divide-gray-200">
                      {[...Array(5)].map((_, index) => (
                        <OpportunityRowSkeleton key={index} />
                      ))}
                    </div>
                  )}

                  {/* Error State */}
                  {opportunityError && !loadingOpportunities && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                      <div className="flex items-start space-x-3">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-medium text-red-800">
                            Error Loading Opportunities
                          </h3>
                          <p className="text-sm text-red-700 mt-1">
                            {opportunityError}
                          </p>
                          <Button
                            variant="outline"
                            onClick={() => refetchOpportunities()}
                            className="mt-4"
                          >
                            Retry
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {!loadingOpportunities &&
                    !opportunityError &&
                    opportunities.length === 0 && (
                      <div className="text-center py-12">
                        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {opportunitySearchTerm.trim()
                            ? "No opportunities found"
                            : "No Opportunities Available"}
                        </h3>
                        <p className="text-gray-600">
                          {opportunitySearchTerm.trim()
                            ? `No opportunities match your search "${opportunitySearchTerm}". Try adjusting your search terms.`
                            : "There are no opportunities available for creating quotes. All opportunities may already have quotes created for them, or there are no active opportunities at this time."}
                        </p>
                      </div>
                    )}

                  {/* Opportunities List */}
                  {!loadingOpportunities &&
                    !opportunityError &&
                    opportunities.length > 0 && (
                      <div className="space-y-3">
                        {opportunities.map((opportunity) => {
                          const companyName = opportunity.company || "Unknown Company";
                          const contactName = `${opportunity.firstName || ""} ${opportunity.lastName || ""}`.trim() || "N/A";
                          const contactEmail = opportunity.email || "";
                          const opportunityValue = "₹" + formatIndianAmount(opportunity.amount);
                          const assigneeName = opportunity.owner
                            ? `${opportunity.owner.firstName || ""} ${opportunity.owner.lastName || ""}`.trim()
                            : "";
                          const assigneeEmail = opportunity.owner?.email || "";

                          return (
                            <div
                              key={opportunity._id || opportunity.id}
                              onClick={() => {
                                // Since backend already filters out opportunities with quotations,
                                // all displayed opportunities are eligible for quote creation
                                handleSelectOpportunity(
                                  opportunity._id || opportunity.id,
                                );
                              }}
                              className={`
                          border border-gray-200 rounded-lg p-4 transition-all duration-200 opportunity-card
                          bg-white hover:bg-agrosaf-green/5 hover:border-agrosaf-green cursor-pointer hover:shadow-md
                        `}
                            >
                              <div className="flex items-start justify-between">
                                {/* Left Section - Company & Contact Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Building className="h-5 w-5 text-agrosaf-green" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3
                                        className="text-base font-semibold text-gray-900 truncate opportunity-title capitalize"
                                        title={companyName}
                                      >
                                        {companyName}
                                      </h3>
                                      <div className="mt-2 flex flex-wrap items-center gap-4">
                                        <div className="flex items-center space-x-2 min-w-0">
                                          <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                          <span
                                            className="text-sm text-gray-700 truncate opportunity-title capitalize"
                                            title={contactName}
                                          >
                                            {contactName}
                                          </span>
                                        </div>
                                        {contactEmail && (
                                          <span
                                            className="text-xs text-gray-500 truncate opportunity-title"
                                            title={contactEmail}
                                          >
                                            {contactEmail}
                                          </span>
                                        )}
                                      </div>
                                      {/* Assignee Info */}
                                      {assigneeName && (
                                        <div className="mt-2 flex items-center gap-2 text-xs bg-blue-50 text-blue-700 rounded-md px-2 py-1 w-fit">
                                          <span className="font-medium">Assignee:</span>
                                          <span className="capitalize">{assigneeName}</span>
                                          {assigneeEmail && (
                                            <span className="text-blue-500">({assigneeEmail})</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Right Section - Value & Status */}
                                <div className="flex items-center space-x-4 ml-4 flex-shrink-0">
                                  <div className="text-right min-w-0">
                                    <div
                                      className="text-lg font-bold text-agrosaf-green truncate opportunity-title"
                                      title={opportunityValue}
                                    >
                                      {opportunityValue}
                                    </div>
                                    {/* Removed the "Quote Already Created" badge since backend filters these out */}
                                  </div>
                                  <div className="flex-shrink-0">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-agrosaf-green/10 text-agrosaf-green">
                                      <CheckCircle className="h-5 w-5" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                  {/* Pagination */}
                  {!loadingOpportunities &&
                    !opportunityError &&
                    opportunities.length > 0 &&
                    (() => {
                      const totalItems =
                        opportunityPaginationData.totalOpportunities ||
                        opportunities.length;
                      const totalPages =
                        opportunityPaginationData.totalPages || 1;
                      const effectiveLimit =
                        opportunityPaginationData.limit ||
                        opportunityItemsPerPage;
                      const shouldShowPagination = totalPages > 1;
                      const currentPage =
                        opportunityPaginationData.page ||
                        opportunityPagination.page ||
                        1;

                      const handlePageChange = (page) => {
                        setOpportunityPagination((prev) => ({ ...prev, page }));
                      };

                      if (!shouldShowPagination) return null;

                      // Generate page numbers
                      const pages = [];
                      if (totalPages <= 10) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);
                        if (currentPage <= 4) {
                          for (let i = 2; i <= 5; i++) {
                            pages.push(i);
                          }
                          pages.push("ellipsis");
                          pages.push(totalPages);
                        } else if (currentPage >= totalPages - 3) {
                          pages.push("ellipsis");
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push("ellipsis");
                          for (
                            let i = currentPage - 1;
                            i <= currentPage + 1;
                            i++
                          ) {
                            pages.push(i);
                          }
                          pages.push("ellipsis");
                          pages.push(totalPages);
                        }
                      }

                      return (
                        <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                            {/* Results Count and Limit Selector */}
                            <div className="flex items-center space-x-4">
                              <div className="text-sm text-gray-700">
                                Showing{" "}
                                {formatIndianNumber(
                                  (currentPage - 1) * effectiveLimit + 1,
                                )}{" "}
                                to{" "}
                                {formatIndianNumber(
                                  Math.min(
                                    currentPage * effectiveLimit,
                                    totalItems,
                                  ),
                                )}{" "}
                                of {formatIndianNumber(totalItems)} results
                              </div>
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-700">
                                  Items per page:
                                </label>
                                <select
                                  value={opportunityItemsPerPage}
                                  onChange={(e) =>
                                    handleOpportunityItemsPerPageChange(
                                      Number(e.target.value),
                                    )
                                  }
                                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                >
                                  <option value={3}>3</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                  <option value={100}>100</option>
                                </select>
                              </div>
                            </div>

                            {/* Pagination Controls */}
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={
                                  !opportunityPagination.hasPrevPage ||
                                  currentPage <= 1
                                }
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Previous
                              </button>

                              {/* Page Numbers */}
                              <div className="flex items-center space-x-1">
                                {pages.map((page, index) => {
                                  if (page === "ellipsis") {
                                    return (
                                      <span
                                        key={`ellipsis-${index}`}
                                        className="px-2 text-gray-500"
                                      >
                                        ...
                                      </span>
                                    );
                                  }

                                  return (
                                    <button
                                      key={page}
                                      onClick={() =>
                                        setOpportunityPagination((prev) => ({
                                          ...prev,
                                          page,
                                        }))
                                      }
                                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPage
                                          ? "bg-agrosaf-green text-white"
                                          : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                        }`}
                                    >
                                      {page}
                                    </button>
                                  );
                                })}
                              </div>

                              <button
                                onClick={() =>
                                  setOpportunityPagination((prev) => ({
                                    ...prev,
                                    page: currentPage + 1,
                                  }))
                                }
                                disabled={
                                  !opportunityPaginationData.hasNextPage ||
                                  currentPage >= totalPages
                                }
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                </>
              )}

              {/* Customers Tab Content */}
              {modalActiveTab === "customers" && (
                <>
                  {/* Loading State */}
                  {loadingCustomers && (
                    <div className="divide-y divide-gray-200">
                      {[...Array(5)].map((_, index) => (
                        <OpportunityRowSkeleton key={index} />
                      ))}
                    </div>
                  )}

                  {/* Error State */}
                  {customerError && !loadingCustomers && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                      <div className="flex items-start space-x-3">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-medium text-red-800">
                            Error Loading Customers
                          </h3>
                          <p className="text-sm text-red-700 mt-1">
                            {customerError}
                          </p>
                          <Button
                            variant="outline"
                            onClick={() => refetchCustomers()}
                            className="mt-4"
                          >
                            Retry
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {!loadingCustomers &&
                    !customerError &&
                    customers.length === 0 && (
                      <div className="text-center py-12">
                        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {customerSearchTerm.trim()
                            ? "No customers found"
                            : "No Customers Available"}
                        </h3>
                        <p className="text-gray-600">
                          {customerSearchTerm.trim()
                            ? `No customers match your search "${customerSearchTerm}". Try adjusting your search terms.`
                            : "There are no customers available for creating quotes at this time."}
                        </p>
                      </div>
                    )}

                  {/* Customers List */}
                  {!loadingCustomers &&
                    !customerError &&
                    customers.length > 0 && (
                      <div className="space-y-3">
                        {customers.map((customer) => {
                          const companyName = customer.companyName || "N/A";
                          const contactPerson = customer.contactPerson || "N/A";
                          const email = customer.email || "";
                          const phone = customer.phone || "";
                          const custAssigneeName = customer.assignedTo
                            ? `${customer.assignedTo.firstName || ""} ${customer.assignedTo.lastName || ""}`.trim()
                            : "";
                          const custAssigneeEmail = customer.assignedTo?.email || "";

                          return (
                            <div
                              key={customer._id || customer.id}
                              onClick={() => {
                                handleSelectCustomer(
                                  customer._id || customer.id,
                                );
                              }}
                              className={`
                              border border-gray-200 rounded-lg p-4 transition-all duration-200
                              bg-white hover:bg-agrosaf-green/5 hover:border-agrosaf-green cursor-pointer hover:shadow-md
                            `}
                            >
                              <div className="flex items-start justify-between">
                                {/* Left Section - Company & Contact Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Building className="h-5 w-5 text-agrosaf-green" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3
                                        className="text-base font-semibold text-gray-900 truncate capitalize"
                                        title={companyName}
                                      >
                                        {companyName}
                                      </h3>
                                      <div className="mt-2 flex flex-wrap items-center gap-4">
                                        <div className="flex items-center space-x-2 min-w-0">
                                          <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                          <span
                                            className="text-sm text-gray-700 truncate capitalize"
                                            title={contactPerson}
                                          >
                                            {contactPerson}
                                          </span>
                                        </div>
                                        {email && (
                                          <span
                                            className="text-xs text-gray-500 truncate"
                                            title={email}
                                          >
                                            {email}
                                          </span>
                                        )}
                                        {phone && (
                                          <span
                                            className="text-xs text-gray-500 truncate"
                                            title={phone}
                                          >
                                            {phone}
                                          </span>
                                        )}
                                      </div>
                                      {/* Assignee Info */}
                                      {custAssigneeName && (
                                        <div className="mt-2 flex items-center gap-2 text-xs bg-blue-50 text-blue-700 rounded-md px-2 py-1 w-fit">
                                          <span className="font-medium">Assignee:</span>
                                          <span className="capitalize">{custAssigneeName}</span>
                                          {custAssigneeEmail && (
                                            <span className="text-blue-500">({custAssigneeEmail})</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Right Section - Customer ID */}
                                <div className="flex items-center space-x-4 ml-4 flex-shrink-0">
                                  <div className="text-right min-w-0">
                                    <div
                                      className="text-sm font-medium text-gray-600 truncate"
                                      title={customer.customerId || "N/A"}
                                    >
                                      {customer.customerId || "N/A"}
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-agrosaf-green/10 text-agrosaf-green">
                                      <CheckCircle className="h-5 w-5" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                  {/* Pagination */}
                  {!loadingCustomers &&
                    !customerError &&
                    customers.length > 0 &&
                    (() => {
                      const totalItems =
                        customerPagination.totalCustomers ||
                        customerPaginationData.total ||
                        customerPaginationData.totalCustomers ||
                        customers.length;
                      const totalPages =
                        customerPagination.totalPages ||
                        customerPaginationData.totalPages ||
                        1;
                      const effectiveLimit =
                        customerPagination.limit ||
                        customerPaginationData.limit ||
                        customerItemsPerPage;
                      const shouldShowPagination = totalPages > 1;
                      const currentPage =
                        customerPagination.page ||
                        customerPaginationData.page ||
                        1;

                      const handlePageChange = (page) => {
                        setCustomerPagination((prev) => ({ ...prev, page }));
                      };

                      if (!shouldShowPagination) return null;

                      // Generate page numbers
                      const pages = [];
                      if (totalPages <= 10) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);
                        if (currentPage <= 4) {
                          for (let i = 2; i <= 5; i++) {
                            pages.push(i);
                          }
                          pages.push("ellipsis");
                          pages.push(totalPages);
                        } else if (currentPage >= totalPages - 3) {
                          pages.push("ellipsis");
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push("ellipsis");
                          for (
                            let i = currentPage - 1;
                            i <= currentPage + 1;
                            i++
                          ) {
                            pages.push(i);
                          }
                          pages.push("ellipsis");
                          pages.push(totalPages);
                        }
                      }

                      return (
                        <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                            {/* Results Count and Limit Selector */}
                            <div className="flex items-center space-x-4">
                              <div className="text-sm text-gray-700">
                                Showing{" "}
                                {formatIndianNumber(
                                  (currentPage - 1) * effectiveLimit + 1,
                                )}{" "}
                                to{" "}
                                {formatIndianNumber(
                                  Math.min(
                                    currentPage * effectiveLimit,
                                    totalItems,
                                  ),
                                )}{" "}
                                of {formatIndianNumber(totalItems)} results
                              </div>
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-700">
                                  Items per page:
                                </label>
                                <select
                                  value={customerItemsPerPage}
                                  onChange={(e) =>
                                    handleCustomerItemsPerPageChange(
                                      Number(e.target.value),
                                    )
                                  }
                                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                >
                                  <option value={3}>3</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                  <option value={100}>100</option>
                                </select>
                              </div>
                            </div>

                            {/* Pagination Controls */}
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={
                                  !customerPagination.hasPrevPage ||
                                  currentPage <= 1
                                }
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Previous
                              </button>

                              {/* Page Numbers */}
                              <div className="flex items-center space-x-1">
                                {pages.map((page, index) => {
                                  if (page === "ellipsis") {
                                    return (
                                      <span
                                        key={`ellipsis-${index}`}
                                        className="px-2 text-gray-500"
                                      >
                                        ...
                                      </span>
                                    );
                                  }

                                  return (
                                    <button
                                      key={page}
                                      onClick={() =>
                                        setCustomerPagination((prev) => ({
                                          ...prev,
                                          page,
                                        }))
                                      }
                                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPage
                                          ? "bg-agrosaf-green text-white"
                                          : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                        }`}
                                    >
                                      {page}
                                    </button>
                                  );
                                })}
                              </div>

                              <button
                                onClick={() =>
                                  setCustomerPagination((prev) => ({
                                    ...prev,
                                    page: currentPage + 1,
                                  }))
                                }
                                disabled={
                                  !customerPaginationData.hasNextPage ||
                                  currentPage >= totalPages
                                }
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end p-6 border-t border-gray-200">
              <Button variant="outline" onClick={handleCloseOpportunityModal}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-lg w-full max-w-md mx-auto shadow-xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Confirm Deletion
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Are you sure you want to delete this quotation?
                </p>
              </div>
              <button
                onClick={handleDeleteCancel}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-700 mb-2">
                  Quotation ID:{" "}
                  <span className="font-medium">
                    {quoteToDelete?.quoteId ||
                      quoteToDelete?.quoteNumber ||
                      "N/A"}
                  </span>
                </p>
                <p className="text-gray-700">
                  Company:{" "}
                  <span className="font-medium capitalize">
                    {quoteToDelete?.companyName || "N/A"}
                  </span>
                </p>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={handleDeleteCancel}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleDeleteConfirm}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Confirm Bulk Deletion
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete{" "}
                <span className="font-bold">{selectedRows.size}</span> selected
                quotation{selectedRows.size > 1 ? "s" : ""}? This action cannot
                be undone.
              </p>
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={isBulkDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmBulkDelete}
                  disabled={isBulkDeleting}
                  icon={
                    isBulkDeleting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )
                  }
                >
                  {isBulkDeleting ? "Deleting..." : "Delete Selected"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Quote Modal */}
      {showSendModal && sendQuote && (
        <SendQuoteModal
          isOpen={showSendModal}
          onClose={() => {
            setShowSendModal(false);
            setSendQuote(null);
          }}
          quote={sendQuote}
          defaultEmail={sendQuote.email || ""} // Use email from projection if available
          onSend={handleExecuteSend}
          isSending={sendQuotationMutation.isPending}
        />
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Quotations"
        showDateRange
        dateRangeAllowFutureEnd
        scopedFormatExport={{
          onExport: handleCreateQuotesScopedExport,
          formats: ["csv", "xlsx", "pdf"],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div>
  );
};

export default CreateQuotesList;
